import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
	Calendar,
	Edit,
	KeyRound,
	Plus,
	Search,
	Trash2,
	Upload,
} from 'lucide-react';

import {
	useDeleteCustomRatesMutation,
	useGetCustomRatesListQuery,
} from '../api';
import { useListPartnersQuery } from '../api/partners';
import { useConfigs } from '../hooks/useConfigs';
import { useFeature } from '../hooks/useFeature';
import { useAuth } from '../providers/AppAuthProvider';

import Loader from './Loader';
import ImportTemplatesModal from './settings/ImportTemplatesModal';
import { formatShortDate, v9 } from './settings/v9';

import type { CustomRatesConfig } from '../api';

// shortcut: filters and paging run in the browser over the first 100 templates
// (the list API's max page size); move them to the API when a tenant has more.
const FETCH_SIZE = 100;
const PER_PAGE = 10;
const MAX_PAGE_BUTTONS = 7;

const MODEL_LABELS: Record<string, string> = {
	IC_PLUS: 'Interchange-Plus',
	TIERED: 'Tiered',
	FLAT_TIERED: 'Flat Tiered',
	FLAT: 'Flat',
};
const MODEL_FILTERS = [
	'Interchange-Plus',
	'Tiered',
	'Flat Tiered',
	'Surcharge',
];
const TIER_FILTERS = ['LOW', 'MEDIUM', 'HIGH', 'OTHER'];
const TEMPLATE_COLUMNS = [
	'Template name',
	'Partner',
	'Rate plan type',
	'Risk tier',
	'Status',
	'Updated',
	'Actions',
];
// Shown only to tenants with the partner_programs feature (BASYS).
const PROGRAM_COLUMNS = ['Partner', 'Risk tier'];
const STATUS_FILTERS = ['Default', 'Custom', 'Draft', 'Archived'];
const STATUS_PILL: Record<string, string> = {
	Default: v9.pillSuccess,
	Custom: v9.pillNeutral,
	Draft: v9.pillWarning,
	Archived: v9.pillNeutral,
};
const TEMPLATE_STATUS_LABELS: Record<string, string> = {
	DRAFT: 'Draft',
	ARCHIVED: 'Archived',
};

const templateStatusLabel = (config: CustomRatesConfig): string =>
	config.is_default
		? 'Default'
		: (TEMPLATE_STATUS_LABELS[config.status ?? ''] ?? 'Custom');

const templateModels = (config: CustomRatesConfig): string[] =>
	(config.selected_rate_types?.length
		? config.selected_rate_types
		: [config.rate_plan_type]
	).map((type) => MODEL_LABELS[type] ?? type);

interface ConfigListProps {
	onSelectConfig?: (configId: string) => void;
	onCreateNew?: () => void;
}

const ConfigList: React.FC<ConfigListProps> = ({
	onSelectConfig,
	onCreateNew,
}) => {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [partner, setPartner] = useState('');
	const [model, setModel] = useState('');
	const [tier, setTier] = useState('');
	const [status, setStatus] = useState('');
	const [showImport, setShowImport] = useState(false);
	const navigate = useNavigate();
	const { isAdmin } = useAuth();

	const { refreshConfigs } = useConfigs();

	const handleSelectConfigInternal = (configId: string) => {
		if (onSelectConfig) {
			onSelectConfig(configId);
		} else {
			navigate(`/settings?edit=${configId}`);
		}
	};

	const handleCreateNewInternal = () => {
		if (onCreateNew) {
			onCreateNew();
		} else {
			navigate('/settings?create=true');
		}
	};

	const {
		data: configsResponse,
		isLoading,
		error,
	} = useGetCustomRatesListQuery({ page: 1, page_size: FETCH_SIZE });
	const partnerPrograms = useFeature('partner_programs');
	const { data: partners = [] } = useListPartnersQuery(undefined, {
		skip: !partnerPrograms,
	});
	const [deleteConfig, { isLoading: isDeleting }] =
		useDeleteCustomRatesMutation();

	const configs = useMemo(() => configsResponse?.data ?? [], [configsResponse]);
	const partnerName = useMemo(
		() => new Map(partners.map((p) => [p.id, p.name])),
		[partners],
	);
	const partnerOf = useCallback(
		(config: CustomRatesConfig) =>
			(config.partner_id && partnerName.get(config.partner_id)) || '',
		[partnerName],
	);

	const rows = useMemo(() => {
		const query = search.trim().toLowerCase();
		return configs.filter(
			(config) =>
				(!query ||
					config.name.toLowerCase().includes(query) ||
					partnerOf(config).toLowerCase().includes(query) ||
					(config.description ?? '').toLowerCase().includes(query)) &&
				(!partner || config.partner_id === partner) &&
				(!model || templateModels(config).includes(model)) &&
				(!tier || config.risk_tier === tier) &&
				(!status || templateStatusLabel(config) === status),
		);
	}, [configs, search, partner, model, tier, status, partnerOf]);

	const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
	const currentPage = Math.min(page, pages);
	const pageRows = rows.slice(
		(currentPage - 1) * PER_PAGE,
		currentPage * PER_PAGE,
	);
	const firstShown = rows.length ? (currentPage - 1) * PER_PAGE + 1 : 0;
	const lastShown = Math.min(rows.length, currentPage * PER_PAGE);

	const filterChange =
		(setter: (value: string) => void) =>
		(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			setter(e.target.value);
			setPage(1);
		};

	const handleDelete = async (configId: string, configName: string) => {
		if (window.confirm(`Are you sure you want to delete "${configName}"?`)) {
			try {
				await deleteConfig(configId).unwrap();
				refreshConfigs();
			} catch (error) {
				console.error('Failed to delete config:', error);
				alert('Failed to delete config. Please try again.');
			}
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader size="xl" text="Loading configurations..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="text-center">
					<div className="text-error-600 text-lg mb-4">
						Error loading configurations
					</div>
					<p className="text-neutral-600">Please try again later.</p>
				</div>
			</div>
		);
	}

	const adminTitle = (title: string) =>
		isAdmin ? title : 'Admin access required';

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-6">
				<div>
					<div className={v9.title}>Pricing templates</div>
					<div className={v9.subtitle}>
						One template per partner program or merchant type, with its rates
						and limits.
					</div>
				</div>
				<div className="flex flex-shrink-0 flex-wrap items-center gap-3">
					<button
						type="button"
						className={v9.btnSecondary}
						onClick={() => navigate('/docs')}
					>
						<KeyRound className={v9.icon} />
						API
					</button>
					<button
						type="button"
						className={v9.btnSecondary}
						onClick={() => setShowImport(true)}
						disabled={!isAdmin}
						title={adminTitle('Import templates')}
					>
						<Upload className={v9.icon} />
						Import templates
					</button>
					<button
						type="button"
						className={v9.btnPrimary}
						onClick={handleCreateNewInternal}
						disabled={!isAdmin}
						title={adminTitle('Create new template')}
					>
						<Plus className={v9.icon} />
						New template
					</button>
				</div>
			</div>

			<div className={v9.card}>
				<div className={v9.cardHead}>
					<div className="relative w-[360px] max-w-full">
						<Search className={v9.searchIcon} />
						<input
							className={v9.searchInput}
							placeholder="Search configurations, partners, verticals…"
							value={search}
							onChange={filterChange(setSearch)}
							aria-label="Search templates"
						/>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						{partnerPrograms && (
							<select
								className={`${v9.filterSelect} w-[210px]`}
								value={partner}
								onChange={filterChange(setPartner)}
								aria-label="Partner"
							>
								<option value="">All partners</option>
								{partners.map((p) => (
									<option key={p.id} value={p.id}>
										{p.name}
									</option>
								))}
							</select>
						)}
						<select
							className={`${v9.filterSelect} w-[180px]`}
							value={model}
							onChange={filterChange(setModel)}
							aria-label="Pricing model"
						>
							<option value="">All pricing models</option>
							{MODEL_FILTERS.map((m) => (
								<option key={m}>{m}</option>
							))}
						</select>
						{partnerPrograms && (
							<select
								className={`${v9.filterSelect} w-[140px]`}
								value={tier}
								onChange={filterChange(setTier)}
								aria-label="Risk tier"
							>
								<option value="">All risk tiers</option>
								{TIER_FILTERS.map((t) => (
									<option key={t}>{t}</option>
								))}
							</select>
						)}
						<select
							className={`${v9.filterSelect} w-[150px]`}
							value={status}
							onChange={filterChange(setStatus)}
							aria-label="Status"
						>
							<option value="">All statuses</option>
							{STATUS_FILTERS.map((s) => (
								<option key={s}>{s}</option>
							))}
						</select>
					</div>
				</div>

				<div className="mt-6 overflow-x-auto">
					<table className="w-full border-collapse">
						<thead>
							<tr>
								{(partnerPrograms
									? TEMPLATE_COLUMNS
									: TEMPLATE_COLUMNS.filter((c) => !PROGRAM_COLUMNS.includes(c))
								).map((heading) => (
									<th key={heading} className={v9.th}>
										{heading}
									</th>
								))}
							</tr>
						</thead>
						<tbody className={v9.tbody}>
							{pageRows.map((config) => {
								const label = templateStatusLabel(config);
								return (
									<tr
										key={config.id}
										className="cursor-pointer hover:bg-gray-50"
										onClick={() => handleSelectConfigInternal(config.id!)}
									>
										<td className={v9.tdPrimary}>{config.name}</td>
										{partnerPrograms && (
											<td className={v9.tdWrap}>{partnerOf(config) || '—'}</td>
										)}
										<td className={v9.td}>
											<span className="flex flex-wrap gap-1.5">
												{templateModels(config).map((m) => (
													<span
														key={m}
														className={`${v9.pill} ${v9.pillPrimary}`}
													>
														{m}
													</span>
												))}
											</span>
										</td>
										{partnerPrograms && (
											<td className={v9.td}>{config.risk_tier || '—'}</td>
										)}
										<td className={v9.td}>
											<span className={`${v9.pill} ${STATUS_PILL[label]}`}>
												{label}
											</span>
										</td>
										<td className={v9.tdMuted}>
											<span className="flex items-center gap-1.5">
												<Calendar className="h-3.5 w-3.5" />
												{formatShortDate(config.updated_at)}
											</span>
										</td>
										<td className={v9.td}>
											<span className="flex items-center gap-2">
												<button
													type="button"
													className={v9.btnSm}
													onClick={(e) => {
														e.stopPropagation();
														handleSelectConfigInternal(config.id!);
													}}
													disabled={!isAdmin}
													title={adminTitle('Edit template')}
												>
													<Edit className={v9.iconSm} />
													Edit
												</button>
												<button
													type="button"
													className={`${v9.btnSm} !text-red-700`}
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(config.id!, config.name);
													}}
													disabled={isDeleting || !isAdmin}
													title={adminTitle('Delete template')}
													aria-label="Delete template"
												>
													<Trash2 className={v9.iconSm} />
												</button>
											</span>
										</td>
									</tr>
								);
							})}
							{pageRows.length === 0 && (
								<tr>
									<td
										colSpan={partnerPrograms ? 7 : 5}
										className={`${v9.tdMuted} py-8 text-center`}
									>
										No configurations match.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className={v9.tableFoot}>
					<span>
						Showing {firstShown}–{lastShown} of {rows.length} configurations
					</span>
					<div className="flex gap-1">
						{Array.from(
							{ length: Math.min(pages, MAX_PAGE_BUTTONS) },
							(_, i) => i + 1,
						).map((n) => (
							<button
								key={n}
								type="button"
								className={n === currentPage ? v9.pagerBtnActive : v9.pagerBtn}
								onClick={() => setPage(n)}
							>
								{n}
							</button>
						))}
						{pages > MAX_PAGE_BUTTONS && (
							<>
								<button type="button" className={v9.pagerBtn} disabled>
									…
								</button>
								<button
									type="button"
									className={
										pages === currentPage ? v9.pagerBtnActive : v9.pagerBtn
									}
									onClick={() => setPage(pages)}
								>
									{pages}
								</button>
							</>
						)}
					</div>
				</div>
			</div>
			<ImportTemplatesModal
				isOpen={showImport}
				onClose={() => setShowImport(false)}
			/>
		</div>
	);
};

export default ConfigList;
